import type { SVGProps } from 'react';

/**
 * Shared outline icons. Each icon renders a 24x24 stroke SVG that inherits `currentColor`.
 * Size via `className` (e.g. `w-5 h-5`) or `width`/`height`; adjust weight with `strokeWidth`.
 */
export type IconProps = SVGProps<SVGSVGElement>;

interface StrokeIconProps extends IconProps {
  paths: readonly string[];
}

function StrokeIcon({ paths, strokeWidth, ...props }: StrokeIconProps) {
  return (
    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={strokeWidth} aria-hidden="true" {...props}>
      {paths.map((d) => (
        <path key={d} strokeLinecap="round" strokeLinejoin="round" d={d} />
      ))}
    </svg>
  );
}

// Navigation
export const HomeIcon = (props: IconProps) => (
  <StrokeIcon
    paths={[
      'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
    ]}
    strokeWidth={1.75}
    {...props}
  />
);
export const CalendarIcon = (props: IconProps) => (
  <StrokeIcon
    paths={['M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z']}
    strokeWidth={1.75}
    {...props}
  />
);
/** Calendar variant with a rounded date header, used for date-of-birth style fields. */
export const CalendarDateIcon = (props: IconProps) => (
  <StrokeIcon
    paths={['M8 3v3m8-3v3M5 10h14M5 7h14a1 1 0 011 1v11a2 2 0 01-2 2H6a2 2 0 01-2-2V8a1 1 0 011-1z']}
    strokeWidth={1.75}
    {...props}
  />
);
export const NewsIcon = (props: IconProps) => (
  <StrokeIcon
    paths={[
      'M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z',
    ]}
    strokeWidth={1.75}
    {...props}
  />
);
export const UsersIcon = (props: IconProps) => (
  <StrokeIcon
    paths={[
      'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z',
    ]}
    strokeWidth={1.75}
    {...props}
  />
);
export const UserIcon = (props: IconProps) => (
  <StrokeIcon
    paths={['M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z']}
    strokeWidth={1.75}
    {...props}
  />
);
export const SettingsIcon = (props: IconProps) => (
  <StrokeIcon
    paths={[
      'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z',
      'M15 12a3 3 0 11-6 0 3 3 0 016 0z',
    ]}
    strokeWidth={1.75}
    {...props}
  />
);
export const PlatformIcon = (props: IconProps) => (
  <StrokeIcon
    paths={[
      'M3 5.75A2.75 2.75 0 015.75 3h12.5A2.75 2.75 0 0121 5.75v12.5A2.75 2.75 0 0118.25 21H5.75A2.75 2.75 0 013 18.25V5.75z',
      'M12 7.5v9M7.5 12h9',
    ]}
    strokeWidth={1.75}
    {...props}
  />
);

// Arrows and chevrons
export const ChevronLeftIcon = (props: IconProps) => (
  <StrokeIcon paths={['M15 19l-7-7 7-7']} strokeWidth={2} {...props} />
);
export const ChevronRightIcon = (props: IconProps) => (
  <StrokeIcon paths={['M9 5l7 7-7 7']} strokeWidth={2} {...props} />
);
export const RefreshIcon = (props: IconProps) => (
  <StrokeIcon
    paths={[
      'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15',
    ]}
    strokeWidth={2}
    {...props}
  />
);
export const ExpandIcon = (props: IconProps) => (
  <StrokeIcon
    paths={['M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5']}
    strokeWidth={1.75}
    {...props}
  />
);
export const ExternalLinkIcon = (props: IconProps) => (
  <StrokeIcon
    paths={['M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3']}
    strokeWidth={2}
    {...props}
  />
);
/** Heroicons v2 "arrow top right on square", used for external link cards. */
export const ArrowTopRightOnSquareIcon = (props: IconProps) => (
  <StrokeIcon
    paths={[
      'M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25',
    ]}
    strokeWidth={1.5}
    {...props}
  />
);

// Actions
export const CheckIcon = (props: IconProps) => <StrokeIcon paths={['M5 13l4 4L19 7']} strokeWidth={2.5} {...props} />;
export const XIcon = (props: IconProps) => <StrokeIcon paths={['M6 18L18 6M6 6l12 12']} strokeWidth={2} {...props} />;
export const PlusIcon = (props: IconProps) => <StrokeIcon paths={['M12 4v16m8-8H4']} strokeWidth={2} {...props} />;
export const EditIcon = (props: IconProps) => (
  <StrokeIcon
    paths={[
      'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z',
    ]}
    strokeWidth={1.75}
    {...props}
  />
);
/** Pencil-and-square variant used for compact inline "change" buttons. */
export const PencilSquareIcon = (props: IconProps) => (
  <StrokeIcon
    paths={[
      'M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7',
      'M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z',
    ]}
    strokeWidth={2}
    {...props}
  />
);
export const CopyIcon = (props: IconProps) => (
  <StrokeIcon
    paths={[
      'M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2',
      'M10 8h8a2 2 0 012 2v8a2 2 0 01-2 2h-8a2 2 0 01-2-2v-8a2 2 0 012-2z',
    ]}
    strokeWidth={2}
    {...props}
  />
);
export const TrashIcon = (props: IconProps) => (
  <StrokeIcon
    paths={[
      'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16',
    ]}
    strokeWidth={1.75}
    {...props}
  />
);
export const SearchIcon = (props: IconProps) => (
  <StrokeIcon paths={['M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z']} strokeWidth={2} {...props} />
);
/** Tray-style download icon (Heroicons v2). */
export const DownloadIcon = (props: IconProps) => (
  <StrokeIcon
    paths={['M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3']}
    strokeWidth={1.75}
    {...props}
  />
);
/** Arrow-into-tray download icon used by media viewers and export buttons. */
export const ArrowDownTrayIcon = (props: IconProps) => (
  <StrokeIcon paths={['M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4m4-5l5 5 5-5m-5 5V3']} strokeWidth={2} {...props} />
);
export const UploadIcon = (props: IconProps) => (
  <StrokeIcon
    paths={['M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5']}
    strokeWidth={1.75}
    {...props}
  />
);
export const ShareIcon = (props: IconProps) => (
  <StrokeIcon paths={['M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8m-4-6l-4-4-4 4m4-4v13']} strokeWidth={2} {...props} />
);
export const LogoutIcon = (props: IconProps) => (
  <StrokeIcon
    paths={['M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1']}
    strokeWidth={2}
    {...props}
  />
);
export const CameraIcon = (props: IconProps) => (
  <StrokeIcon
    paths={[
      'M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z',
      'M15 13a3 3 0 11-6 0 3 3 0 016 0z',
    ]}
    strokeWidth={2}
    {...props}
  />
);
export const ContactSaveIcon = (props: IconProps) => (
  <StrokeIcon
    paths={['M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2', 'M8.5 11a4 4 0 100-8 4 4 0 000 8zM20 8v6M17 11h6']}
    strokeWidth={1.75}
    {...props}
  />
);

// Status
export const CheckCircleIcon = (props: IconProps) => (
  <StrokeIcon paths={['M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z']} strokeWidth={2} {...props} />
);
export const XCircleIcon = (props: IconProps) => (
  <StrokeIcon
    paths={['M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z']}
    strokeWidth={2}
    {...props}
  />
);
export const QuestionCircleIcon = (props: IconProps) => (
  <StrokeIcon
    paths={[
      'M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
    ]}
    strokeWidth={2}
    {...props}
  />
);
export const ClockIcon = (props: IconProps) => (
  <StrokeIcon paths={['M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z']} strokeWidth={1.75} {...props} />
);
export const LockIcon = (props: IconProps) => (
  <StrokeIcon
    paths={['M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z']}
    strokeWidth={1.75}
    {...props}
  />
);
export const ShieldIcon = (props: IconProps) => (
  <StrokeIcon
    paths={[
      'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
    ]}
    strokeWidth={2}
    {...props}
  />
);
export const BellIcon = (props: IconProps) => (
  <StrokeIcon
    paths={[
      'M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0',
    ]}
    strokeWidth={1.75}
    {...props}
  />
);
export const InfoCircleIcon = (props: IconProps) => (
  <StrokeIcon paths={['M12 22a10 10 0 100-20 10 10 0 000 20z', 'M12 16v-4M12 8h.01']} strokeWidth={1.75} {...props} />
);

// Content and places
export const LocationIcon = (props: IconProps) => (
  <StrokeIcon
    paths={[
      'M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z',
      'M15 11a3 3 0 11-6 0 3 3 0 016 0z',
    ]}
    strokeWidth={1.75}
    {...props}
  />
);
export const ArchiveIcon = (props: IconProps) => (
  <StrokeIcon
    paths={['M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4']}
    strokeWidth={1.75}
    {...props}
  />
);
export const InboxIcon = (props: IconProps) => (
  <StrokeIcon
    paths={[
      'M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4',
    ]}
    strokeWidth={1.75}
    {...props}
  />
);
export const DocumentIcon = (props: IconProps) => (
  <StrokeIcon
    paths={[
      'M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z',
    ]}
    strokeWidth={1.75}
    {...props}
  />
);
export const DocumentTextIcon = (props: IconProps) => (
  <StrokeIcon
    paths={[
      'M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z',
    ]}
    strokeWidth={1.5}
    {...props}
  />
);
export const PhotoIcon = (props: IconProps) => (
  <StrokeIcon
    paths={[
      'M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M6.75 19.5h10.5a2.25 2.25 0 002.25-2.25V6.75a2.25 2.25 0 00-2.25-2.25H6.75A2.25 2.25 0 004.5 6.75v10.5a2.25 2.25 0 002.25 2.25z',
    ]}
    strokeWidth={1.5}
    {...props}
  />
);
export const GlobeIcon = (props: IconProps) => (
  <StrokeIcon
    paths={[
      'M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9',
    ]}
    strokeWidth={1.75}
    {...props}
  />
);
export const EmailIcon = (props: IconProps) => (
  <StrokeIcon
    paths={['M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z']}
    strokeWidth={1.75}
    {...props}
  />
);
export const PhoneIcon = (props: IconProps) => (
  <StrokeIcon
    paths={[
      'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z',
    ]}
    strokeWidth={1.75}
    {...props}
  />
);
export const BuildingIcon = (props: IconProps) => (
  <StrokeIcon
    paths={[
      'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
    ]}
    strokeWidth={1.75}
    {...props}
  />
);
/** Heroicons v2 office building, used on organization selection screens. */
export const BuildingOfficeIcon = (props: IconProps) => (
  <StrokeIcon
    paths={[
      'M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21',
    ]}
    strokeWidth={1.5}
    {...props}
  />
);
export const BriefcaseIcon = (props: IconProps) => (
  <StrokeIcon
    paths={[
      'M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
    ]}
    strokeWidth={1.75}
    {...props}
  />
);
export const AcademicIcon = (props: IconProps) => (
  <StrokeIcon
    paths={[
      'M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222',
    ]}
    strokeWidth={1.75}
    {...props}
  />
);
export const SparklesIcon = (props: IconProps) => (
  <StrokeIcon
    paths={['M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z']}
    strokeWidth={1.75}
    {...props}
  />
);
export const HeartIcon = (props: IconProps) => (
  <StrokeIcon
    paths={[
      'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z',
    ]}
    strokeWidth={1.75}
    {...props}
  />
);
export const UserBadgeIcon = (props: IconProps) => (
  <StrokeIcon
    paths={['M5.121 17.804A9 9 0 1118.88 17.8M15 10a3 3 0 11-6 0 3 3 0 016 0zM19.5 19.5l2 2']}
    strokeWidth={1.75}
    {...props}
  />
);
export const OrgSettingsIcon = (props: IconProps) => <BuildingIcon {...props} />;
export const IntegrationIcon = (props: IconProps) => (
  <StrokeIcon
    paths={[
      'M7.5 7.5h-.75A2.25 2.25 0 004.5 9.75v.75m0 3v.75a2.25 2.25 0 002.25 2.25h.75m9-9h.75a2.25 2.25 0 012.25 2.25v.75m0 3v.75a2.25 2.25 0 01-2.25 2.25h-.75M9 12h6m-3-3v6',
    ]}
    strokeWidth={1.75}
    {...props}
  />
);

/** Solid star used for highlighted roles. */
export function StarIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
      <path d="M12 2.8l2.74 5.55 6.13.89-4.44 4.33 1.05 6.11L12 16.8l-5.48 2.88 1.05-6.11-4.44-4.33 6.13-.89L12 2.8z" />
    </svg>
  );
}

/** Circular-arrows rotate icon used by image editors. */
export function RotateIcon({ size = 14, ...props }: IconProps & { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <path d="M1 4v6h6" />
      <path d="M23 20v-6h-6" />
      <path d="M20.49 9A9 9 0 005.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 013.51 15" />
    </svg>
  );
}
